import React, { useState } from "react";
import { CheckCircle, X, Sparkle, FileText } from "@phosphor-icons/react";

function diffArrays(a = [], b = []) {
    const A = new Set((a || []).map((x) => x.toLowerCase()));
    const B = new Set((b || []).map((x) => x.toLowerCase()));
    const added = (b || []).filter((x) => !A.has(x.toLowerCase()));
    const removed = (a || []).filter((x) => !B.has(x.toLowerCase()));
    const same = (a || []).filter((x) => B.has(x.toLowerCase()));
    return { added, removed, same };
}

const FIELDS = [
    { key: "name", label: "Name" },
    { key: "title", label: "Headline / title" },
    { key: "location", label: "Location" },
    { key: "experience_years", label: "Years of experience" },
    { key: "skills", label: "Skills", isArray: true },
    { key: "bio", label: "Bio" },
];

export default function ResumeParseDialog({ open, onClose, parsed, current, onApply }) {
    const initial = {};
    FIELDS.forEach((f) => {
        const cur = current?.[f.key];
        const par = parsed?.[f.key];
        // accept by default if parsed has a non-empty value AND it's different from current
        const hasNew = f.isArray
            ? Array.isArray(par) && par.length > 0
            : par !== undefined && par !== null && par !== "" && par !== 0;
        const isDifferent = JSON.stringify(cur || (f.isArray ? [] : "")) !== JSON.stringify(par || (f.isArray ? [] : ""));
        initial[f.key] = hasNew && isDifferent;
    });
    const [accepted, setAccepted] = useState(initial);

    if (!open) return null;

    const apply = () => {
        const payload = {};
        FIELDS.forEach((f) => {
            if (accepted[f.key]) {
                if (f.isArray) {
                    // merge unique
                    const merged = Array.from(
                        new Set([...(current?.skills || []), ...(parsed?.skills || [])].map((x) => x.trim()))
                    ).filter(Boolean);
                    payload[f.key] = merged;
                } else if (f.key === "experience_years") {
                    payload[f.key] = Number(parsed?.[f.key]) || 0;
                } else {
                    payload[f.key] = parsed?.[f.key] || "";
                }
            }
        });
        onApply(payload);
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-slate-950/70 flex items-center justify-center p-4"
            data-testid="resume-parse-dialog"
        >
            <div className="bg-white max-w-3xl w-full max-h-[88vh] overflow-auto border border-slate-300">
                <div className="border-b border-slate-200 p-5 flex items-start justify-between gap-4">
                    <div>
                        <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-1 inline-flex items-center gap-1.5">
                            <Sparkle size={14} weight="fill" /> AI résumé parser
                        </div>
                        <h2 className="font-display text-2xl">Review what we extracted</h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Pick the fields you want to apply. Skills are merged with what you already have.
                        </p>
                    </div>
                    <button
                        data-testid="dialog-close"
                        onClick={onClose}
                        className="p-1.5 hover:bg-slate-100"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="divide-y divide-slate-200">
                    {FIELDS.map((f) => {
                        const cur = current?.[f.key];
                        const par = parsed?.[f.key];
                        const empty = f.isArray
                            ? !Array.isArray(par) || par.length === 0
                            : par === undefined || par === null || par === "" || par === 0;

                        return (
                            <div
                                key={f.key}
                                data-testid={`parse-field-${f.key}`}
                                className={`p-5 flex items-start gap-4 ${empty ? "opacity-50" : ""}`}
                            >
                                <button
                                    type="button"
                                    data-testid={`parse-toggle-${f.key}`}
                                    onClick={() => setAccepted((s) => ({ ...s, [f.key]: !s[f.key] }))}
                                    disabled={empty}
                                    className={`mt-1 w-6 h-6 border flex items-center justify-center shrink-0 ${
                                        accepted[f.key]
                                            ? "bg-blue-600 border-blue-600 text-white"
                                            : "border-slate-300 hover:border-slate-950"
                                    }`}
                                >
                                    {accepted[f.key] && <CheckCircle size={16} weight="fill" />}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline justify-between gap-2 mb-1">
                                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                                            {f.label}
                                        </div>
                                        {empty && (
                                            <div className="text-[10px] uppercase tracking-widest text-slate-400">
                                                not detected
                                            </div>
                                        )}
                                    </div>
                                    {f.isArray ? (
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {(() => {
                                                const { added, same, removed } = diffArrays(cur, par);
                                                return (
                                                    <>
                                                        {same.map((s) => (
                                                            <span key={"s" + s} className="text-xs px-2 py-0.5 border border-slate-300 font-mono-plex">
                                                                {s}
                                                            </span>
                                                        ))}
                                                        {added.map((s) => (
                                                            <span key={"a" + s} className="text-xs px-2 py-0.5 bg-blue-600 text-white font-mono-plex">
                                                                + {s}
                                                            </span>
                                                        ))}
                                                        {removed.length > 0 && (
                                                            <span className="text-[10px] text-slate-400 self-center font-mono-plex">
                                                                ({removed.length} unchanged in your profile)
                                                            </span>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1 text-sm">
                                            <div className="border border-slate-200 p-2 bg-slate-50">
                                                <div className="text-[10px] font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                                                    Current
                                                </div>
                                                <div className="text-slate-700 break-words">
                                                    {cur || <span className="text-slate-400">—</span>}
                                                </div>
                                            </div>
                                            <div className="border border-blue-600 p-2">
                                                <div className="text-[10px] font-mono-plex uppercase tracking-widest text-blue-600 mb-1">
                                                    From résumé
                                                </div>
                                                <div className="text-slate-900 break-words">
                                                    {par || <span className="text-slate-400">—</span>}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="border-t border-slate-200 p-5 flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                        <FileText size={14} /> Résumé saved to your account
                    </div>
                    <div className="flex gap-2">
                        <button
                            data-testid="parse-cancel-btn"
                            onClick={onClose}
                            className="px-4 py-2 border border-slate-300 hover:bg-slate-100"
                        >
                            Skip
                        </button>
                        <button
                            data-testid="parse-apply-btn"
                            onClick={apply}
                            className="px-4 py-2 bg-blue-600 text-white hover:bg-slate-950 inline-flex items-center gap-1.5"
                        >
                            <CheckCircle size={16} /> Apply selected
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
